import { OperationDescriptor } from "relay-runtime";
import { MockPayloadGenerator } from "relay-test-utils";
import { hubs, products } from "./__mocks__";

// Store for tracking unlock request states
const unlockRequestStore: Record<
	string,
	{
		id: string;
		status: "PENDING" | "EXECUTED" | "UNLOCKED" | "TIMED_OUT" | "ERROR";
		createdAt: number;
		reservation: any;
		error?: string;
	}
> = {};

const findHubByName = (hubName: string) => {
	const normalizedHubName = hubName.toLowerCase().replace(/-/g, " ");

	return (
		hubs.page.nodes.find(
			(hub) => hub.meta?.name?.toLowerCase() === normalizedHubName || hub.meta?.name?.toLowerCase() === hubName.toLowerCase()
		) ?? hubs.Default
	);
};

type OperationData = (variables: Record<string, unknown>) => object;
const operationData = new Map<string, OperationData>();

/** Answers the named operations with fixed data instead of generated values (e.g. for guide stories). */
export const setOperationData = (data: Record<string, OperationData>) => {
	for (const [name, resolve] of Object.entries(data)) operationData.set(name, resolve);
};

/** Mocked answer for GraphQL sent outside Relay (`fetchGraphQL`): only operations registered with `setOperationData`. */
export const mockRawOperation = (query: string, variables: Record<string, unknown>) => {
	const operationName = /\b(?:query|mutation)\s+(\w+)/.exec(query)?.[1];
	const fixedData = operationName ? operationData.get(operationName) : undefined;
	if (!fixedData) throw new Error(`No mock data for ${operationName ?? "unnamed operation"}`);
	return { data: fixedData(variables) };
};

export const mockOperation = (operation: OperationDescriptor) => {
	// Log the operation being executed
	const operationName = operation.request.node.operation.name;
	const fixedData = operationData.get(operationName);
	if (fixedData) {
		console.log(`📌 [Relay Mock] Fixed data for: ${operationName}`);
		const data = fixedData(operation.request.variables);
		return MockPayloadGenerator.generate(operation, { Query: () => data, Mutation: () => data });
	}
	console.group(`🔍 [Relay Mock] Executing: ${operationName}`);
	console.log("Variables:", operation.request.variables);
	console.log("Query text:", (operation.request.node as any).params?.text);
	console.groupEnd();

	const variables = operation.request.variables as any;

	// Handle DoorUnlockPollingQuery
	if (operationName === "DoorUnlockPollingQuery" && variables.unlockRequestId) {
		const unlockRequest = unlockRequestStore[variables.unlockRequestId];

		if (unlockRequest) {
			console.log(`📊 [Mock] UnlockRequest status: ${unlockRequest.status}, Space: ${unlockRequest.reservation.space.status}`);
			return {
				data: {
					unlockRequest: {
						id: unlockRequest.id,
						status: unlockRequest.status,
						reservation: unlockRequest.reservation,
						error: unlockRequest.error || null,
						unlockedAt: unlockRequest.status === "UNLOCKED" ? new Date().toISOString() : null,
					},
				},
			};
		}

		// Default unlock request if not found
		console.log(`⚠️ [Mock] UnlockRequest not found: ${variables.unlockRequestId}`);
		return {
			data: {
				unlockRequest: {
					id: variables.unlockRequestId,
					status: "ERROR",
					reservation: {
						id: "unknown",
						space: {
							id: "unknown",
							status: "CLOSED",
						},
					},
					error: "Unlock request not found",
					unlockedAt: null,
				},
			},
		};
	}

	// Check if this is a HubScannerDrawerQuery
	if (variables.hubName && operationName === "HubScannerDrawerQuery") {
		console.log(`🎯 [Mock] Handling HubScannerDrawerQuery with hub name: ${variables.hubName}`);

		const hubData = findHubByName(variables.hubName);

		const mockHubMeta = {
			id: hubData.meta?.id || "hub-meta-alice",
			name: variables.hubName === "alice" ? "Alice" : hubData.meta?.name || "Alice",
			hub: {
				id: hubData.id,
				spaces: hubData.spaces.map((space) => ({
					id: space.id,
					frame: space.frame,
				})),
			},
		};

		console.log(`✅ [Mock] Returning custom mock for hubMeta:`, mockHubMeta);
		return {
			data: {
				hubMeta: mockHubMeta,
			},
		};
	}

	// Default MockPayloadGenerator for other queries
	return MockPayloadGenerator.generate(
		operation,
		{
			Hub(context) {
				console.log(`📊 [Mock Resolver] Hub query with args:`, context.args);
				return hubs.page.nodes.find((hub) => hub.id === context.args?.id);
			},
			HubPage() {
				console.log(`📊 [Mock Resolver] HubPage query`);
				return hubs.page;
			},
			hubs() {
				console.log(`📊 [Mock Resolver] hubs query`);
				return hubs.page;
			},
			HubProductPage() {
				console.log(`📊 [Mock Resolver] HubProductPage query`);
				return products.page;
			},
			HubMeta(context) {
				console.log(`📊 [Mock Resolver] HubMeta query with args:`, context.args);
				const by = context.args?.by as any;
				if (by?.name) {
					return findHubByName(by.name).meta;
				}
				return hubs.Default.meta;
			},
			hubMeta(context) {
				console.log(`📊 [Mock Resolver] hubMeta query with args:`, context.args);
				const by = context.args?.by as any;
				if (by?.name) {
					return findHubByName(by.name).meta;
				}
				return hubs.Default.meta;
			},
			Query() {
				console.log(`📊 [Mock Resolver] Query root type`);
				return {};
			},
		},
		{ mockClientData: true }
	);
};
